<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5">
    <FormSelect label="Service" v-model="config.service" :options="serviceOptions" default-reducer :clearable="false" class="w-full" @update:modelValue="onServiceChange" />
    <div v-if="!service" class="text-12 text-semi-dark-text mt-2">Choose how the data is exported. The source and destination options depend on the service.</div>

    <template v-if="service">
      <!-- Source -->
      <div class="text-12 font-600 border-b border-solid border-gray-200 pb-1 mt-6">Source</div>
      <FormSelect label="Source adapter" :clearable="true" v-model="config.adapter_type" :options="sourceAdapters" default-reducer class="mt-3 w-full" @update:modelValue="onSourceChange" />
      <FormSelect
        v-if="config.adapter_type"
        label="Tool"
        :clearable="true"
        v-model="config.tool_name"
        :options="toolsList"
        default-reducer
        class="mt-3 w-full single-line-select"
        :loading="toolsFetching"
        @update:modelValue="onToolChange"
      />
      <div v-if="selectedTool && !supportsIncremental" class="text-12 mt-2 text-yellow-600">This tool has no {since} parameter, so every run exports everything.</div>

      <!-- {since} and {start_date} are filled in by the export itself, so they are shown but not mapped. -->
      <div v-for="parameter in systemParameters" :key="parameter.name" class="border border-solid border-stroke rounded-8 p-3 mt-3 bg-on-tertiary">
        <div class="flex items-start justify-between gap-x-3">
          <div class="min-w-0">
            <div class="font-600 text-13 truncate">{{ parameter.name }}</div>
            <div class="text-12 text-semi-dark-text mt-1">{{ parameter.explanation }}</div>
          </div>
          <Badge text="Set automatically" small />
        </div>
      </div>

      <div v-for="parameter in toolParameters" :key="parameter.name" class="border border-solid border-stroke rounded-8 p-3 mt-3">
        <div class="flex items-start justify-between gap-x-3">
          <div class="min-w-0">
            <div class="font-600 text-13 truncate">{{ parameter.name }}</div>
            <div class="text-12 text-semi-dark-text mt-1">{{ parameter.description || parameter.type }}</div>
          </div>
          <Badge v-if="parameter.required" text="Required" warn small />
        </div>
        <div class="flex mt-3 gap-x-3 flex-wrap">
          <FormSelect
            v-model="config.arguments[parameter.name].assignment_type"
            label="Assign"
            :options="assignmentTypes"
            default-reducer
            :clearable="false"
            class="w-150 flex-shrink-0"
            @update:modelValue="onArgumentTypeChange(parameter)"
          />
          <div v-if="config.arguments[parameter.name].assignment_type === 'variable'" class="flex-grow min-w-200">
            <VariableSelect label="Variable" v-model="config.arguments[parameter.name].value" @update:modelValue="emitPayload" />
          </div>
          <div v-else class="flex-grow min-w-200">
            <PhraseEditor v-model="config.arguments[parameter.name].value" :items="variables" label="Value" class="w-full" @update:modelValue="emitPayload" />
          </div>
        </div>
      </div>

      <!-- Destination and the service's own settings, kept under the service's own key -->
      <div class="text-12 font-600 border-b border-solid border-gray-200 pb-1 mt-6">Destination</div>
      <FormSelect
        label="Destination adapter"
        :clearable="true"
        v-model="config.destination_adapter"
        :options="destinationAdapters"
        default-reducer
        class="mt-3 w-full"
        @update:modelValue="emitPayload"
      />
      <component :is="service.component" :key="config.service" v-model="config[service.configKey]" :tool-columns="toolColumns" @update:modelValue="emitPayload" />

      <!-- Options -->
      <div class="text-12 font-600 border-b border-solid border-gray-200 pb-1 mt-6">Options</div>
      <FormSelect label="Mode" v-model="config.mode" :options="modes" default-reducer :clearable="false" class="mt-3 w-full" @update:modelValue="emitPayload" />
      <FormInput v-model="config.start_date" label="Start date (optional)" placeholder="YYYY-MM-DD or {Variable}" class="mt-3 w-full" @update:modelValue="emitPayload" />
      <div class="text-12 text-semi-dark-text mt-1">Passed to the tool as {start_date}. Leave empty to export all data.</div>
      <VariableSelect label="Assign written files to variable (optional)" v-model="config.variable" class="mt-3 w-full" system :filter-data-type="['DataTable']" @update:modelValue="emitPayload" />

      <!-- Test -->
      <div class="flex items-center justify-between text-12 font-600 border-b border-solid border-gray-200 pb-1 mt-6">
        <span>Test</span>
        <div class="flex gap-x-2">
          <PillButton v-if="testResult || testError" text="Clear" small outlined @click="clearTest" />
          <PillButton text="Test export" small primary :loading="testing" :disabled="!canTest" @click="testExport" />
        </div>
      </div>
      <div class="text-12 text-semi-dark-text mt-2">
        Runs the tool on up to 1,000 rows as a first run would, using the project variables' initial values. The rows are converted and checked against the columns, and nothing is uploaded.
      </div>
      <div v-if="testError" class="text-12 text-error mt-2">{{ testError }}</div>
      <div v-if="testResult" class="mt-3 flex flex-col gap-y-3">
        <div class="text-12">
          {{ testResult.rows_total }} row{{ testResult.rows_total !== 1 ? 's' : '' }} converted in {{ testResult.duration_ms }} ms.
          <span v-if="testResult.truncated" class="text-yellow-600">Only a sample was read, the full export will contain more.</span>
          <span v-if="!testResult.rows_total" class="text-yellow-600">The query returned no rows{{ testResult.start_date ? ` from ${testResult.start_date}` : '' }}.</span>
        </div>
        <div v-if="testResult.files?.length">
          <div class="text-12 font-700 mb-1">Files that would be written</div>
          <div v-for="file in testResult.files" :key="file.path" class="text-11 break-all">{{ file.path }} ({{ file.rows }} rows, {{ formatBytes(file.bytes) }})</div>
        </div>
        <div v-if="testResult.sample_rows?.length">
          <div class="text-12 font-700 mb-1">Sample rows</div>
          <div class="overflow-auto border border-solid border-stroke rounded-8 max-h-300">
            <table class="text-11 w-full">
              <thead>
                <tr>
                  <th v-for="column in testResult.columns" :key="column" class="text-left font-600 px-2 py-1 bg-on-tertiary whitespace-nowrap">{{ column }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in testResult.sample_rows" :key="index" class="border-t border-solid border-stroke">
                  <td v-for="column in testResult.columns" :key="column" class="px-2 py-1 whitespace-nowrap">{{ formatCell(row[column]) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Settings panel for the data export block. Handles what every export shares: the
 * service, source adapter, tool and arguments, destination adapter, mode, start date
 * and output variable. The chosen service's own settings are edited by that service's
 * panel and stored under the service's own payload key (e.g. parquet_config), so each
 * service's settings stay separate.
 */
import { mapActions, mapGetters, mapState } from 'vuex';
import { DATA_ADAPTERS } from '@/constants';
import { apiPostProjectDataExportTest } from '@/helpers/api';
import debounce from '../../../helpers/debounce';
import uuidv4 from '../../../helpers/uuid';
import EvaluateItemsConfig from './EvaluateItemsConfig';
import SERVICES from './data-export/services';
import { columnsFromTool } from './data-export/apache-parquet';

// Filled in by the export itself, never mapped by hand.
const SYSTEM_PARAMS = ['since', 'start_date'];
const TOOL_LABEL_MAX_LENGTH = 90;

/**
 * Escapes text for the select, which renders option labels as HTML.
 * @param {string} text Plain text.
 * @returns {string} HTML-safe text.
 */
function escapeHtml(text) {
  return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * A tool's dropdown label on one line: the name, then the start of its description.
 * @param {{name: string, description?: string}} tool Tool definition.
 * @returns {string} HTML label, e.g. "<b>customer_data</b> - Customer export for…".
 */
function singleLineToolLabel(tool) {
  const description = String(tool.description || '')
    .replace(/\s+/g, ' ')
    .trim();
  const room = TOOL_LABEL_MAX_LENGTH - tool.name.length;
  const short = description.length > room ? `${description.slice(0, Math.max(room - 1, 0)).trimEnd()}…` : description;
  return `<b>${escapeHtml(tool.name)}</b>${short ? ` - ${escapeHtml(short)}` : ''}`;
}

export default {
  name: 'DataExportConfig',
  props: {
    /** The evaluate item being edited: { id, type, payload }. */
    item: {
      type: Object,
      default: null,
    },
  },
  emits: ['update', 'valid'],
  /**
   * @returns {{config: object, tools: object[], toolsFetching: boolean, testing: boolean, testResult: object|null, testError: string}}
   *   Editable payload, the source adapter's tools and the last test's outcome.
   */
  data() {
    return {
      config: structuredClone(EvaluateItemsConfig['data-export'].payload),
      tools: [],
      toolsFetching: false,
      testing: false,
      testResult: null,
      testError: '',
    };
  },
  computed: {
    ...mapState(['projectData']),
    ...mapGetters(['getProjectDataAdapters']),
    /**
     * @returns {string} The project being edited.
     */
    projectId() {
      return this.$route.params.projectId;
    },
    /**
     * @returns {object|null} The chosen export service, or null when none is chosen.
     */
    service() {
      return SERVICES[this.config.service] || null;
    },
    /**
     * @returns {Array<{label: string, value: string}>} Every available export service.
     */
    serviceOptions() {
      return Object.values(SERVICES).map((service) => ({ label: service.name, value: service.key }));
    },
    /**
     * @returns {Array<{label: string, value: string}>} Export modes.
     */
    modes() {
      return [
        { label: 'Incremental: everything first, then only changes', value: 'incremental' },
        { label: 'Full: everything on every run', value: 'full' },
      ];
    },
    /**
     * @returns {Array<{label: string, value: string}>} Project adapters the service can read from.
     */
    sourceAdapters() {
      return this.adaptersOfTypes(this.service?.sources || []);
    },
    /**
     * @returns {Array<{label: string, value: string}>} Project adapters the service can write to.
     */
    destinationAdapters() {
      return this.adaptersOfTypes(this.service?.destinations || []);
    },
    /**
     * @returns {Array<{label: string, value: string}>} The source adapter's tools.
     */
    toolsList() {
      return this.tools.map((tool) => ({ label: singleLineToolLabel(tool), value: tool.name }));
    },
    /**
     * @returns {Array<{name: string, explanation: string}>} The export parameters the chosen tool uses, which the block fills in itself.
     */
    systemParameters() {
      const properties = this.selectedTool?.parameters?.properties || {};
      const explanations = {
        since:
          this.config.mode === 'full'
            ? 'Always empty in Full mode, so every run exports everything.'
            : "Empty on the first run, so everything is exported. Later runs pass the previous run's snapshot time, so only changes are exported.",
        start_date: 'Taken from the Start date option below. Empty when no start date is set.',
      };
      return SYSTEM_PARAMS.filter((name) => properties[name]).map((name) => ({ name, explanation: explanations[name] }));
    },
    /**
     * @returns {object|null} The chosen tool's definition.
     */
    selectedTool() {
      return this.tools.find((tool) => tool.name === this.config.tool_name) || null;
    },
    /**
     * @returns {boolean} Whether the tool declares {since}, so incremental runs export only changes.
     */
    supportsIncremental() {
      return Boolean(this.selectedTool?.parameters?.properties?.since);
    },
    /**
     * @returns {object[]} The chosen tool's output columns as block columns, with types suggested from the
     *   database types the tool returns. Empty when the tool does not declare its columns.
     */
    toolColumns() {
      return columnsFromTool(this.selectedTool?.output?.columns);
    },
    /**
     * @returns {boolean} Whether a test can run. The destination is not needed, as nothing is uploaded.
     */
    canTest() {
      const c = this.config;
      return Boolean(this.service && c.adapter_type && c.tool_name && this.service.validate(c[this.service.configKey]));
    },
    /**
     * @returns {object[]} The tool's parameters that are mapped by hand, excluding {since} and {start_date}.
     */
    toolParameters() {
      const properties = this.selectedTool?.parameters?.properties || {};
      const required = this.selectedTool?.parameters?.required || [];
      return Object.keys(properties)
        .filter((name) => !SYSTEM_PARAMS.includes(name))
        .map((name) => ({ name, required: required.includes(name), ...properties[name] }));
    },
    /**
     * @returns {Array<{label: string, value: string}>} How a tool argument is assigned.
     */
    assignmentTypes() {
      return [
        { label: this.$t('variable'), value: 'variable' },
        { label: this.$t('value'), value: 'value' },
      ];
    },
    /**
     * @returns {Array<{id: string, value: string}>} Project variables for the phrase editor, sorted by name.
     */
    variables() {
      return Object.keys(this.projectData?.[this.projectId]?.variables || {})
        .map((variable) => ({ id: uuidv4(), value: variable }))
        .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));
    },
  },
  created() {
    this.itemChangeHandler();
  },
  methods: {
    ...mapActions(['fetchProjectDataAdapterTools']),
    /**
     * Lists the project's adapters of the given kinds. The store holds adapters in two
     * shapes: from the project load (adapter_type and name, no config, so no `type`) and
     * from the adapter list (the config, with `type` and payload.name). Both are handled.
     * @param {string[]} types DATA_ADAPTERS keys.
     * @returns {Array<{label: string, value: string}>} Matching adapters.
     */
    adaptersOfTypes(types) {
      return Object.entries(this.getProjectDataAdapters())
        .map(([key, adapter]) => ({ key, adapter, type: adapter.type || adapter.adapter_type || key }))
        .filter(({ type }) => types.includes(type))
        .map(({ key, adapter, type }) => ({ label: adapter.payload?.name || adapter.name || DATA_ADAPTERS[type]?.name || key, value: key }));
    },
    /**
     * Reports validity and the edited payload to the flow editor, debounced.
     * @returns {void}
     */
    emitPayload: debounce(function emitExportPayload() {
      this.$emit('valid', this.validate());
      this.$emit('update', { id: this.item.id, type: this.item.type, payload: JSON.parse(JSON.stringify(this.config)) });
    }, 300),
    /**
     * Makes sure the chosen service has its own settings in the payload.
     * @returns {void}
     */
    ensureServiceConfig() {
      if (this.service && !this.config[this.service.configKey]) this.config[this.service.configKey] = this.service.defaultConfig();
    },
    /**
     * Switches service. The new service gets its own settings (kept if it was used
     * before), other services' settings are left untouched, and adapters the new service
     * cannot use are cleared.
     * @returns {void}
     */
    onServiceChange() {
      this.ensureServiceConfig();
      if (!this.sourceAdapters.some((option) => option.value === this.config.adapter_type)) this.onSourceChange();
      if (!this.destinationAdapters.some((option) => option.value === this.config.destination_adapter)) this.config.destination_adapter = '';
      this.emitPayload();
    },
    /**
     * Loads the source adapter's tools.
     * @returns {Promise<void>} Resolves once `tools` is set.
     */
    async fetchTools() {
      this.tools = [];
      if (!this.config.adapter_type) return;
      this.toolsFetching = true;
      const response = await this.fetchProjectDataAdapterTools({ projectId: this.projectId, adapterType: this.config.adapter_type });
      this.toolsFetching = false;
      this.tools = response?.tools || [];
    },
    /**
     * Resets the tool and its arguments when the source adapter changes.
     * @returns {Promise<void>} Resolves once the new tools are loaded.
     */
    async onSourceChange() {
      this.config.tool_name = '';
      this.config.arguments = {};
      await this.fetchTools();
      this.emitPayload();
    },
    /**
     * Resets the arguments to match the newly chosen tool.
     * @returns {void}
     */
    onToolChange() {
      this.config.arguments = {};
      this.toolParameters.forEach((parameter) => this.ensureArgumentBinding(parameter));
      this.emitPayload();
    },
    /**
     * Adds an empty argument binding for a parameter that has none.
     * @param {{name: string}} parameter Tool parameter.
     * @returns {void}
     */
    ensureArgumentBinding(parameter) {
      if (!this.config.arguments[parameter.name]) {
        this.config.arguments[parameter.name] = { assignment_type: 'value', value: '' };
      }
    },
    /**
     * Clears an argument's value when it switches between variable and value.
     * @param {{name: string}} parameter Tool parameter.
     * @returns {void}
     */
    onArgumentTypeChange(parameter) {
      this.config.arguments[parameter.name].value = '';
      this.emitPayload();
    },
    /**
     * Dry runs the export from the current, possibly unsaved, settings. Project
     * variables' initial values stand in for their runtime values.
     * @returns {Promise<void>} Resolves once `testResult` or `testError` is set.
     */
    async testExport() {
      this.clearTest();
      this.testing = true;
      const projectVariables = this.projectData?.[this.projectId]?.variables || {};
      const variables = Object.fromEntries(Object.entries(projectVariables).map(([name, variable]) => [name, variable?.value ?? '']));
      const response = await apiPostProjectDataExportTest({ project_id: this.projectId, payload: JSON.parse(JSON.stringify(this.config)), variables });
      this.testing = false;
      if (response.status === 200 && response.data?.success) this.testResult = response.data;
      else this.testError = response.data?.message || 'The export test failed. Try again shortly.';
    },
    /**
     * Clears the last test's outcome.
     * @returns {void}
     */
    clearTest() {
      this.testResult = null;
      this.testError = '';
    },
    /**
     * @param {number} bytes A file size.
     * @returns {string} The size in B, KB or MB.
     */
    formatBytes(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },
    /**
     * @param {*} value A sample row's value.
     * @returns {string} The value for display, with nulls shown as "null".
     */
    formatCell(value) {
      if (value === null || value === undefined) return 'null';
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
    },
    /**
     * Whether the block can run: a service, source adapter and tool, required
     * arguments, a destination adapter, and complete settings for the chosen service.
     * @returns {boolean} True when valid.
     */
    validate() {
      const c = this.config;
      if (!this.service || !c.adapter_type || !c.tool_name || !c.destination_adapter) return false;
      if (!this.toolParameters.every((parameter) => !parameter.required || Boolean(c.arguments[parameter.name]?.value))) return false;
      return this.service.validate(c[this.service.configKey]);
    },
    /**
     * Loads the item's payload into the editor.
     * @returns {Promise<void>} Resolves once the source tools are loaded.
     */
    async itemChangeHandler() {
      if (!this.item) return;
      this.clearTest();
      const defaults = structuredClone(EvaluateItemsConfig['data-export'].payload);
      this.config = { ...defaults, ...JSON.parse(JSON.stringify(this.item.payload || {})) };
      this.ensureServiceConfig();
      await this.fetchTools();
      if (this.config.tool_name) this.toolParameters.forEach((parameter) => this.ensureArgumentBinding(parameter));
      this.$emit('valid', this.validate());
    },
  },
  watch: {
    item: {
      /**
       * Reloads when a different block is opened.
       * @returns {void}
       */
      handler() {
        this.itemChangeHandler();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
/* Keeps the chosen tool on one line: the label shrinks and ends with an ellipsis instead of wrapping. */
.single-line-select :deep(.vs__selected-options) {
  flex-wrap: nowrap;
  min-width: 0;
}
.single-line-select :deep(.vs__selected) {
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
